import { Help } from '@mui/icons-material'
import {
  Box,
  Checkbox,
  Divider,
  Grid,
  SelectChangeEvent,
  Stack,
  TextField,
  Tooltip,
  Typography,
  styled,
  useMediaQuery,
  useTheme
} from '@mui/material'
import { grey } from '@mui/material/colors'
import dayjs, { Dayjs } from 'dayjs'
import 'dayjs/locale/es'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Calendar from '../../components/Calendar'
import FancyInput from '../../components/FancyInput'
import MessageManager from '../../components/MessageManager'
import Progress from '../../components/Progress'
import StepsBtn from '../../components/StepsBtn'
import TextInformation from '../../components/TextInformation'
import { useApp } from '../../hooks/useApp'
import '../../i18n'
import { CONSTANT } from '../../utils/Constants'
import {
  convertTo24HourFormat,
  filterHours,
  formatSingleHourTo12Hour,
  formatTo12Hour
} from '../../utils/FilterHours'
import { formatNumber } from '../../utils/commons'
import { useBookingCapacity } from '../../queries/useBookingQueries'
import { useAsociado } from '../../queries/useUtilQueries'
import { setFormDataKey, useFormData } from '../../queries/userGlobalQueries'
import { queryClient } from '../../queries/config'
import { useSpaces } from '../../queries/useCompanyQueries'
import CustomMultiSelect from '../../components/CustomMultiSelect'

const InputContainerStiled = styled(Box)(({ theme }) => ({
  gridTemplateColumns: 'repeat(2, 1fr)',
  alignItems: 'center',
  justifyItems: 'start',
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: 'repeat(3, 1fr)',
    '& > p': {
      gridColumn: '1/3'
    },
    '&.inputColegiate': {
      gridTemplateColumns: '100%'
    }
  }
}))

const BoxSchedulesStyled = styled(Box)(({ theme }) => ({
  '& .MuiPickersCalendarHeader-root ': {
    justifyContent: 'space-between',
    padding: theme.spacing(0, 8),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(0, 2)
    }
  },
  '& .MuiPickersCalendarHeader-labelContainer': {
    height: 60,
    maxHeight: 60,
    marginRight: 0
  },
  '& .MuiPickersCalendarHeader-label': {
    color: theme.palette.mode === 'dark' ? '#ffffff' : 'inherit',
    fontSize: 20,
    height: 35,
    textTransform: 'capitalize',
    [theme.breakpoints.down('sm')]: {
      fontSize: 'calc(10px + 2vw)',
      height: 22,
      marginRight: 0
    }
  },
  '& .MuiIconButton-root': {
    color: theme.palette.mode === 'dark' ? '#ffffff' : 'inherit'
  },
  '& .MuiCalendarPicker-root': {
    width: 600,
    maxWidth: '100%',
    maxHeight: 550,
    [theme.breakpoints.down('sm')]: {
      maxHeight: 380
    }
  },
  '& .MuiTypography-caption': {
    color: theme.palette.mode === 'dark' ? '#ffffff' : 'inherit',
    width: 61,
    height: 25,
    margin: 0,
    fontSize: 18,
    [theme.breakpoints.down('sm')]: {
      width: 'calc(10px + 8vw)'
    }
  },
  '& .MuiDayPicker-slideTransition': {
    minHeight: 200,
    [theme.breakpoints.down('sm')]: {
      minHeight: 220
    }
  },
  '& .MuiPickersDay-root': {
    backgroundColor: theme.palette.mode === 'dark' ? '#1e1e1e' : '#ffffff',
    color: theme.palette.mode === 'dark' ? '#ffffff' : 'inherit',
    width: 47,
    height: 35,
    fontSize: 14,
    border: 'solid 5px #fff',
    borderLeftWidth: 7,
    borderRightWidth: 7,
    borderTopWidth: 2,
    borderBottomWidth: 2,
    margin: '0 8px',
    '&:hover': {
      backgroundColor:
        theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)'
    },
    '&.Mui-selected': {
      backgroundColor: theme.palette.primary.main,
      color: '#ffffff',
      '&:hover': {
        backgroundColor: theme.palette.primary.light
      }
    },
    '&.MuiPickersDay-today:not(.Mui-selected)': {
      border: 'solid 5px #fff',
      borderLeftWidth: 12,
      borderRightWidth: 12,
      backgroundColor:
        theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.15)'
    },
    [theme.breakpoints.down('sm')]: {
      width: 'calc(5px + 8vw)',
      height: 40,
      borderLeftWidth: 3,
      borderRightWidth: 3,
      '&.MuiPickersDay-today:not(.Mui-selected)': {
        borderLeftWidth: 3,
        borderRightWidth: 3
      }
    }
  },
  '& .MuiCalendarOrClockPicker-root>div': {
    maxHeight: 440,
    width: 650,
    maxWidth: '100%'
  },
  '& .MuiPickerStaticWrapper-content': {
    minWidth: 'auto'
  },
  '& .MuiPickersArrowSwitcher-spacer': {
    [theme.breakpoints.down('sm')]: {
      width: 4
    }
  }
}))

const Schedules = ({
  handleBack,
  handleNext,
  colegial,
  setColegial,
  setDisabledFullday,
  disabledFullday,
  haveAssociated,
  currency
}: any) => {
  const { language, iDCompany, setErrorMsg, errorMsg, resetErrorMsg } = useApp()

  const { data: formData } = useFormData()

  const { t } = useTranslation()
  const theme = useTheme()
  const isMd = useMediaQuery(theme.breakpoints.down('md'))
  const isXl = useMediaQuery(theme.breakpoints.up('xl'))
  const [valueDate, setValueDate] = useState<Dayjs | null>(
    formData.bookingDay ? dayjs(formData.bookingDay) : null
  )
  const [selectedHours, setSelectedHours] = useState<string[]>([])
  const [hoursAvailable, setHoursAvailable] = useState<any>(undefined)
  const [hoursAvailableCapacity, setHoursAvailableCapacity] = useState<any>(null)
  const [checkBox, setCheckBox] = useState<boolean>(
    formData?.isColegial ? formData.isColegial : false
  )
  const [checkBoxFullDay, setCheckBoxFullDay] = useState<boolean>(
    formData.fullDay ? formData.fullDay : false
  )
  const [colegialnro, setColegialnro] = useState(formData.nroColegial ? formData.nroColegial : '')
  const [errorColegial, setErrorColegial] = useState<string | boolean>(' ')
  const [areaDetail, setAreaDetail] = useState<any>(null)
  const [idArea, setIdArea] = useState<any>(null)
  const [areaCapacity, setAreaCapacity] = useState<any>(null)
  const [hourValidationAlert, setHourValidationAlert] = useState(false)
  const [selectOpen, setSelectOpen] = useState(false)

  const [spacesInput, setSpacesInput] = useState<string>('')

  const { data: areasData } = useSpaces()

  const type = formData.areaType

  // Query para capacidad de reserva
  const {
    data: capacityData,
    isLoading: isLoadingCapacity,
    refetch: refetchBookingCapacity
  } = useBookingCapacity(
    iDCompany?.body?.content[0]?.id || 0,
    valueDate?.format('YYYY-MM-DD') || '',
    idArea || 0,
    areaCapacity && areaCapacity > 0 ? undefined : areaDetail?.id
  )

  // Query para asociado
  const { data: asociadoData, isLoading: isLoadingAsociado } = useAsociado(
    colegialnro || '',
    iDCompany?.body?.content[0]?.url || ''
  )

  useEffect(() => {
    setAreaDetail(
      areasData?.body[formData.area]?.areaDetailList.find((item: any) => {
        return item.description === formData.desc
      })
    )
    const idArea = areasData?.body[formData.area]?.id
    const capacity = areasData?.body[formData.area]?.capacity

    if (idArea) {
      setIdArea(idArea)
    }

    if (capacity) {
      setAreaCapacity(capacity)
    }
  }, [])

  // En useEffect o donde inicialices selectedHours
  useEffect(() => {
    if (formData.hours && formData.hours.length > 0) {
      if (type === 'transportation') {
        // Para transportation, convertimos horas completas a formato solo inicio
        const displayHours = formData.hours.map((fullHour: any) => {
          const [start] = fullHour.split(' - ')
          const startParts = start.split(':')
          let hour = parseInt(startParts[0])
          const ampm = hour >= 12 ? 'PM' : 'AM'
          hour = hour % 12 || 12
          return `${hour}:${startParts[1]} ${ampm}`
        })
        setSelectedHours(displayHours)
      } else {
        // Comportamiento original
        setSelectedHours(formData.hours.map((hour) => formatTo12Hour(hour)))
      }
    }
  }, [formData.hours, type])

  const hourValidation = checkBoxFullDay ? false : selectedHours.length === 0
  const colegiateValidation = formData.isColegial
    ? colegialnro.length === 0 || hourValidation
    : false

  const disabledNextBtn =
    !formData.bookingDay ||
    hourValidation ||
    colegiateValidation ||
    ((areaCapacity || areaDetail?.hasCapacity) &&
      (!formData.spaces ||
        formData.spaces <= 0 ||
        formData.spaces > (areaCapacity ? areaCapacity : areaDetail?.capacity)))

  useEffect(() => {
    document.documentElement.scrollTop = 0
  }, [])

  const getHours = async (dateValue: Dayjs | null) => {
    try {
      if (!dateValue) {
        return
      }

      await refetchBookingCapacity()

      const valueDayWeek: number = Number(dateValue.format('d'))
      const isWeekend: boolean = valueDayWeek === 0 || valueDayWeek === 6

      if (capacityData?.content) {
        const hoursAvailable = capacityData.content
          .filter((hourData: any) => hourData.availableCapacity > 0)
          .map((hourData: any) => {
            const label = `${hourData.hourStart} - ${hourData.hourEnd}`
            return {
              label,
              available: true,
              availableCapacity: hourData.availableCapacity
            }
          })

        setHoursAvailable(hoursAvailable)
        // Filtrar los horarios según la capacidad seleccionada
        if (formData.spaces && formData.spaces > 0 && areaDetail?.hasCapacity) {
          const filteredHours = hoursAvailable.filter(
            (hour: any) => hour.availableCapacity >= formData.spaces
          )
          setHoursAvailableCapacity(filteredHours)
        } else {
          setHoursAvailableCapacity(hoursAvailable)
        }
        queryClient.setQueryData([setFormDataKey], (old: FormData) => ({
          ...old,
          bookingDay: dateValue.format('YYYY-MM-DD'),
          isWeekend
        }))
      }
    } catch (error) {
      console.error('Error al obtener horas disponibles:', error)
    }
  }

  const handleCheckbox = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCheckBox(event.target.checked)
    queryClient.setQueryData([setFormDataKey], (old: FormData) => ({
      ...old,
      isColegial: event.target.checked
    }))
    if (!event.target.checked) {
      setColegial('')
      setColegialnro('')
    }
  }

  const handleChangeFullDay = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCheckBoxFullDay(event.target.checked)
    queryClient.setQueryData([setFormDataKey], (old: FormData) => ({
      ...old,

      fullDay: event.target.checked
    }))
  }

  const handleHoursChange = (event: SelectChangeEvent<any>) => {
    // For transportation type, ensure we only get a single value
    let selectedHoursArray

    if (type === 'transportation') {
      // For transportation, convert to array if it's a single value
      selectedHoursArray =
        typeof event.target.value === 'string' ? [event.target.value] : event.target.value

      // Ensure only one item is selected for transportation
      if (Array.isArray(selectedHoursArray) && selectedHoursArray.length > 1) {
        // Take only the most recently selected item
        selectedHoursArray = [selectedHoursArray[selectedHoursArray.length - 1]]
      }
    } else {
      // Original behavior for non-transportation
      selectedHoursArray = Array.isArray(event.target.value) ? event.target.value : []
    }

    // Obtener la fecha actual
    const currentDate = dayjs()

    // Obtener la fecha seleccionada
    const selectedDate = valueDate ? dayjs(valueDate) : null

    // Verificar si la fecha seleccionada es la fecha actual
    const isCurrentDate = selectedDate?.isSame(currentDate, 'day')

    // const hourEarliest = 4;
    const hourEarliest = iDCompany?.body.content[0].hourEarlier

    // Obtener la hora permitida en base a hourEarlier
    const allowedHour = currentDate.hour() + hourEarliest

    // Verificar si hay alguna hora seleccionada y si la fecha seleccionada es la fecha actual
    if (selectedHoursArray.length > 0 && isCurrentDate) {
      // Verificar si alguna de las horas seleccionadas excede el límite permitido
      const exceedLimit = selectedHoursArray.some((hourRange: any) => {
        // Para tipo transportation, necesitamos recuperar el rango completo
        let fullHourRange = hourRange
        if (type === 'transportation' && hoursAvailable) {
          // Buscar el item original que coincide con esta hora seleccionada
          const originalItem = hoursAvailable.find((item: any) => {
            const displayValue = formatTo12Hour(item.label, type)
            return displayValue === hourRange
          })
          if (originalItem) {
            fullHourRange = originalItem.label // Usar el rango completo original
          }
        }

        const hours24format = convertTo24HourFormat(fullHourRange)
        const [startHour] = hours24format.split(' - ')

        // Obtener la hora de inicio en formato numérico
        const startHourNum = parseInt(startHour.split(':')[0])

        // Verificar si la hora de inicio excede el límite permitido
        if (startHourNum <= allowedHour) {
          if (hourEarliest > 1) {
            setErrorMsg && setErrorMsg(`${t('onlyReserve')} ${hourEarliest} ${t('hoursBefore')}`)
          } else {
            setErrorMsg && setErrorMsg(`${t('onlyReserve')} ${hourEarliest} ${t('hourBefore')}`)
          }
          setSelectOpen(false)
          return true
        }
        return false
      })

      if (exceedLimit) {
        // Si alguna hora excede el límite permitido, mostrar alerta
        setHourValidationAlert(exceedLimit)
        setSelectedHours([])
      } else {
        // Para tipo transportation, necesitamos manejar de manera especial
        if (type === 'transportation') {
          // Mapear las horas seleccionadas a sus rangos completos
          const fullHourRanges = selectedHoursArray.map((hourDisplay: any) => {
            // Buscar el item original que coincide con esta hora seleccionada
            const originalItem = hoursAvailable.find((item: any) => {
              const displayValue = formatTo12Hour(item.label, type)
              return displayValue === hourDisplay
            })
            return originalItem ? originalItem.label : hourDisplay
          })

          // Convertir al formato de 24 horas
          const selectedHours24Format = fullHourRanges.map(convertTo24HourFormat)
          selectedHours24Format.sort()

          // Actualizar formData con los rangos completos en formato 24h
          queryClient.setQueryData([setFormDataKey], (old: FormData) => ({
            ...old,

            hours: selectedHours24Format
          }))

          // Mantener solo las horas de visualización para el usuario
          setSelectedHours(selectedHoursArray)
        } else {
          // Código original para tipos no-transportation
          const selectedHours24Format = selectedHoursArray.map(convertTo24HourFormat)
          selectedHours24Format.sort()
          const selectedHours12Format = selectedHours24Format.map((time: any) =>
            formatTo12Hour(time)
          )
          queryClient.setQueryData([setFormDataKey], (old: FormData) => ({
            ...old,
            hours: selectedHours24Format
          }))
          setSelectedHours(selectedHours12Format)
        }
      }
    } else {
      setHourValidationAlert(false)

      // Manejar tipo transportation
      if (type === 'transportation') {
        // Similar lógica que arriba para mantener rangos completos internamente
        const fullHourRanges = selectedHoursArray.map((hourDisplay: any) => {
          const originalItem = hoursAvailable.find((item: any) => {
            const displayValue = formatTo12Hour(item.label, type)
            return displayValue === hourDisplay
          })
          return originalItem ? originalItem.label : hourDisplay
        })

        const selectedHours24Format = fullHourRanges.map(convertTo24HourFormat)
        selectedHours24Format.sort()
        queryClient.setQueryData([setFormDataKey], (old: FormData) => ({
          ...old,
          hours: selectedHours24Format
        }))

        setSelectedHours(selectedHoursArray)
      } else {
        // Código original
        const selectedHours24Format = selectedHoursArray.map(convertTo24HourFormat)
        selectedHours24Format.sort()
        const selectedHours12Format = selectedHours24Format.map((time: any) => formatTo12Hour(time))

        queryClient.setQueryData([setFormDataKey], (old: FormData) => ({
          ...old,
          hours: selectedHours24Format
        }))
        setSelectedHours(selectedHours12Format)
      }
    }
  }

  const validateColegial = async (data: string) => {
    setErrorColegial(false)
    if (formData.isColegial) {
      if (asociadoData?.status === 200) {
        if (asociadoData.body.length > 0) {
          if (asociadoData.body[0].status === 0) {
            setColegial(asociadoData.body[0])
            setErrorColegial('')
          } else {
            setErrorColegial(`${t('error1Associated')}`)
          }
        } else {
          setColegial('')
          setErrorColegial(`${t('error2Associated')}`)
        }
      } else {
        setErrorColegial(`${t('error3Associated')}`)
      }
    } else {
      setErrorColegial('')
    }
  }

  const onSubmit = async () => {
    setErrorColegial(false)
    if (formData.isColegial && colegial.name === '') {
      if (asociadoData?.status === 200) {
        if (asociadoData.body.length > 0) {
          if (asociadoData.body[0].status === 0) {
            setColegial(asociadoData.body[0])
          } else {
            setErrorColegial(`${t('error1Associated')}`)
          }
        } else {
          setErrorColegial(`${t('error2Associated')}`)
        }
      } else {
        setErrorColegial(`${t('error3Associated')}`)
      }
    } else {
      handleNext()
    }
  }

  useEffect(() => {
    if (
      areaDetail &&
      areaDetail.capacity !== undefined &&
      areaDetail.capacity !== 0 &&
      areaCapacity
    ) {
      queryClient.setQueryData([setFormDataKey], (old: FormData) => ({
        ...old,
        spaces: areaDetail.capacity
      }))
      setSpacesInput(areaDetail.capacity.toString())
    }
  }, [areaDetail])

  function filterHours(hoursAvailable: any) {
    if (!hoursAvailable) {
      return
    } else if (!areaDetail?.hasCapacity) {
      setHoursAvailableCapacity(hoursAvailable)
    } else {
      const filteredHours = hoursAvailable.filter(
        (hour: any) => hour.availableCapacity >= formData.spaces
      )
      setHoursAvailableCapacity(filteredHours)
    }
  }

  useEffect(() => {
    if (formData.spaces != 0 && formData.spaces != null) {
      filterHours(hoursAvailable)

      // Solo vaciamos los horarios seleccionados si la capacidad ya no es suficiente
      if (
        hoursAvailable &&
        areaDetail?.hasCapacity &&
        formData.hours &&
        formData.hours.length > 0
      ) {
        const selectedHoursStillAvailable = formData.hours.every((hourRange: string) => {
          // Encontrar el horario correspondiente en hoursAvailable
          const hourAvailable = hoursAvailable.find(
            (hour: any) =>
              hour.label === hourRange || formatTo12Hour(hour.label) === formatTo12Hour(hourRange)
          )
          // Verificar si tiene capacidad suficiente
          return hourAvailable && hourAvailable.availableCapacity >= formData.spaces
        })

        // Si algún horario seleccionado ya no tiene capacidad suficiente, vaciamos la selección
        if (!selectedHoursStillAvailable) {
          setSelectedHours([])

          queryClient.setQueryData([setFormDataKey], (old: FormData) => ({
            ...old,
            hours: []
          }))
        }
      }

      // Si ya hay una fecha seleccionada pero no hay horarios disponibles cargados,
      // volvemos a cargar los horarios
      if (valueDate && (!hoursAvailable || hoursAvailable.length === 0)) {
        getHours(valueDate)
      }
    }
  }, [formData.spaces])

  // Efecto para validar el número colegial cuando cambia asociadoData o colegialnro
  useEffect(() => {
    if (formData.isColegial && colegialnro.length > 0 && !isLoadingAsociado) {
      validateColegial(colegialnro)
    }
  }, [asociadoData, colegialnro, formData.isColegial, isLoadingAsociado])

  // Efecto para actualizar los horarios disponibles cuando cambia capacityData
  useEffect(() => {
    if (capacityData?.content && valueDate) {
      const hoursAvailable = capacityData.content
        .filter((hourData: any) => hourData.availableCapacity > 0)
        .map((hourData: any) => {
          const label = `${hourData.hourStart} - ${hourData.hourEnd}`
          return {
            label,
            available: true,
            availableCapacity: hourData.availableCapacity
          }
        })

      setHoursAvailable(hoursAvailable)

      // Filtrar los horarios según la capacidad seleccionada
      if (formData.spaces && formData.spaces > 0 && areaDetail?.hasCapacity) {
        const filteredHours = hoursAvailable.filter(
          (hour: any) => hour.availableCapacity >= formData.spaces
        )
        setHoursAvailableCapacity(filteredHours)
      } else {
        setHoursAvailableCapacity(hoursAvailable)
      }
    }
  }, [capacityData])

  return (
    <>
      <Grid container spacing={4} justifyContent={'center'}>
        <Grid
          item
          xs={12}
          lg={8}
          sx={{
            borderRight: {
              lg: '1px solid'
            },
            pr: 3
          }}
        >
          {isMd || isXl ? (
            <TextInformation title={`${t('scheduleTheDay')}`} />
          ) : (
            <Divider sx={{ my: 4, height: '1px' }} />
          )}
          <BoxSchedulesStyled>
            <Typography variant="h4" align="center" gutterBottom sx={{ mt: 2 }}>
              {formData.desc}
            </Typography>
            <Calendar
              value={valueDate}
              onChange={(date) => {
                setValueDate(date)
                queryClient.setQueryData([setFormDataKey], (old: FormData) => ({
                  ...old,
                  bookingDay: date ? date.format('YYYY-MM-DD') : null
                }))
              }}
              onChangeComplete={(date) => {
                getHours(date)
                setCheckBoxFullDay(false)
              }}
            />
          </BoxSchedulesStyled>
          <Stack hidden={!formData.bookingDay} sx={{ mt: 2 }}>
            <InputContainerStiled
              sx={{ display: areaDetail?.reservationType ? 'grid' : 'none' }}
              hidden={areaDetail?.reservationType}
            >
              <Typography variant="body1">{t('allDay')}</Typography>
              <Box display={'flex'} alignItems="center">
                <Checkbox
                  disabled={disabledFullday}
                  checked={checkBoxFullDay}
                  onChange={handleChangeFullDay}
                  sx={{ ml: -1.5, '& .MuiSvgIcon-root': { fontSize: 32 } }}
                />
                {disabledFullday && (
                  <Tooltip title={'Ya exiten horas reservadas este día'}>
                    <Help color={'primary'} />
                  </Tooltip>
                )}
              </Box>
            </InputContainerStiled>
            <CustomMultiSelect
              disabled={checkBoxFullDay}
              multiselect={type !== 'transportation'}
              listItems={
                hoursAvailableCapacity
                  ? hoursAvailableCapacity.map((item: any) => {
                      // Guardar el valor completo para referencias internas
                      const fullLabel = item.label
                      // Mostrar solo la hora de inicio para transportation
                      const displayLabel = formatTo12Hour(item.label, type)

                      return {
                        ...item,
                        label: displayLabel,
                        // Para transportation, usamos displayLabel como value para selección
                        // Ya que es lo que el usuario ve y selecciona
                        value:
                          type === 'transportation'
                            ? displayLabel
                            : convertTo24HourFormat(fullLabel),
                        // Guardamos el valor completo para referencia
                        fullValue: convertTo24HourFormat(fullLabel)
                      }
                    })
                  : []
              }
              value={selectedHours}
              onChange={handleHoursChange}
              label={`${t('schedules')}`}
            />
          </Stack>
          {errorColegial && (
            <Typography variant="body1" align="center" fontWeight={600} color="error" mt={2}>
              {errorColegial}
            </Typography>
          )}
          {(isLoadingCapacity || isLoadingAsociado) && <Progress padding="0" size={40} />}

          <StepsBtn
            onClickBack={handleBack}
            onClickNext={onSubmit}
            disabledNext={disabledNextBtn}
            TextButtonNext={
              formData.isColegial && colegial.name === '' ? t('consultCollegiate') : t('continue')
            }
          />
        </Grid>

        <Grid item xs={12} lg={4} sx={{ pt: '65px!important' }}>
          <Typography variant="h5" align="center" gutterBottom>
            {t('schedules')}
          </Typography>
          {areaDetail?.startTime !== areaDetail?.startTimeWeekend ||
          areaDetail?.endTime !== areaDetail?.endTimeWeekend ? (
            <>
              <Typography variant="subtitle1" align="center">
                {t('mondayToFriday')}
              </Typography>
              <Typography variant="subtitle1" align="center">
                {formatSingleHourTo12Hour(areaDetail?.startTime)} -{' '}
                {formatSingleHourTo12Hour(areaDetail?.endTime)}
              </Typography>
              <Typography variant="subtitle1" align="center">
                {t('saturdayAndSunday')}
              </Typography>
              <Typography variant="subtitle1" align="center">
                {formatSingleHourTo12Hour(areaDetail?.startTimeWeekend)} -{' '}
                {formatSingleHourTo12Hour(areaDetail?.endTimeWeekend)}
              </Typography>
            </>
          ) : (
            <>
              <Typography variant="subtitle1" align="center">
                {t('mondayAndSunday')}
              </Typography>
              <Typography variant="subtitle1" align="center">
                {areaDetail?.startTime} - {areaDetail?.endTime}
              </Typography>
            </>
          )}
          <Typography variant="h5" align="center" sx={{ pt: 4 }} gutterBottom>
            {t('price')}
          </Typography>
          {haveAssociated && (
            <Typography variant="subtitle1" align="center">
              {formatNumber(areaDetail?.associatedAmount, currency)} {t('associated')}
            </Typography>
          )}

          <Typography variant="subtitle1" align="center">
            {formatNumber(areaDetail?.publicAmount, currency)} {haveAssociated && t('particular')}
          </Typography>

          {(areaCapacity || (areaDetail && areaDetail.hasCapacity)) && (
            <Stack>
              <TextField
                label={t('spaces')}
                type="number"
                value={spacesInput || formData.spaces || ''}
                onChange={(event) => {
                  const newValue = parseInt(event.target.value)
                  if (newValue >= 0 || event.target.value === '') {
                    setSpacesInput(event.target.value)
                    queryClient.setQueryData([setFormDataKey], (old: FormData) => ({
                      ...old,
                      spaces: event.target.value === '' ? '' : newValue
                    }))
                  }
                }}
                fullWidth
                margin="normal"
                required
                disabled={
                  areaDetail &&
                  areaDetail.capacity !== undefined &&
                  areaDetail.capacity !== 0 &&
                  areaCapacity
                }
                error={
                  formData.spaces === 0 ||
                  (!areaCapacity && !(areaDetail && areaDetail.capacity !== undefined)) ||
                  formData.spaces >
                    (areaCapacity ? areaCapacity : areaDetail && areaDetail.capacity)
                }
                helperText={
                  (!areaCapacity && !(areaDetail && areaDetail.capacity !== undefined)) ||
                  formData.spaces >
                    (areaCapacity ? areaCapacity : areaDetail && areaDetail.capacity)
                    ? t('errorSpaces')
                    : formData.spaces === 0
                    ? t('requiredField')
                    : ''
                }
              />
            </Stack>
          )}
          {haveAssociated && (
            <>
              <Box sx={{ border: `solid 2px ${grey[200]}`, padding: '8px 24px 24px', mt: 4 }}>
                <InputContainerStiled sx={{ display: 'grid', gridTemplateColumns: '75% 25% ' }}>
                  <Typography variant="body1">{t('isAssociated')}</Typography>
                  <Checkbox
                    checked={checkBox}
                    onChange={handleCheckbox}
                    sx={{ ml: -1.5, '& .MuiSvgIcon-root': { fontSize: 32 } }}
                  />
                </InputContainerStiled>
                <Box hidden={!formData.isColegial}>
                  <InputContainerStiled className="inputColegiate" sx={{ display: 'grid' }}>
                    <Typography variant="body1">{t('nAssociated')}</Typography>
                    <FancyInput
                      type={'number'}
                      fullWidth
                      required={formData.isColegial}
                      value={colegialnro}
                      onChange={(event) => {
                        if (event.target.value.length === 0) {
                          setColegialnro('')
                          queryClient.setQueryData([setFormDataKey], (old: FormData) => ({
                            ...old,
                            nroColegial: ''
                          }))
                          setErrorColegial('')
                        } else {
                          const inputValue = event.target.value

                          setColegialnro(inputValue)
                          queryClient.setQueryData([setFormDataKey], (old: FormData) => ({
                            ...old,
                            nroColegial: inputValue
                          }))
                        }
                      }}
                    />
                  </InputContainerStiled>
                </Box>
              </Box>
              {colegial.name !== '' && (
                <Typography
                  variant="h6"
                  textAlign={'center'}
                  color="primary"
                  fontWeight={'bold'}
                  sx={{ mt: 3 }}
                >
                  {colegial.name}
                </Typography>
              )}
            </>
          )}
        </Grid>
      </Grid>
      {hourValidationAlert && (
        <MessageManager
          type={CONSTANT.ERROR_MSG}
          msg={t('modifyReservationTime')}
          details={errorMsg}
          callback={resetErrorMsg}
          color="primary.main"
        />
      )}
    </>
  )
}

export default Schedules
